/**
 * Helper functions for AuditSessionsFilters component
 *
 * Extracted to reduce component complexity and improve maintainability.
 */

import type { MultiSelectOption } from '@soteria/design-system-ve';

import type { AuditSessionFilters, FilterOptions } from './types';
import { STATUS_FILTER_OPTIONS } from './types';

/**
 * Check if any filters are currently active
 */
export function hasActiveFilters(filters: AuditSessionFilters): boolean {
  return filters.status.length > 0 || filters.labels.length > 0 || filters.topics.length > 0 || filters.languages.length > 0;
}

/**
 * Count the total number of active filters
 */
export function getActiveFilterCount(filters: AuditSessionFilters): number {
  return filters.status.length + filters.labels.length + filters.topics.length + filters.languages.length;
}

/**
 * Convert status options to MultiSelect format
 */
export function createStatusOptions(): MultiSelectOption[] {
  return STATUS_FILTER_OPTIONS.map(option => ({
    id: option.id,
    label: option.label,
    value: option.value,
  }));
}

/**
 * Convert string array to MultiSelect options
 */
export function createMultiSelectOptions(items: string[]): MultiSelectOption[] {
  return items.map(item => ({
    id: item,
    label: item,
    value: item,
  }));
}

/**
 * Create all filter options for MultiSelect components
 */
export function createFilterOptions(filterOptions: FilterOptions) {
  return {
    statusOptions: createStatusOptions(),
    labelOptions: createMultiSelectOptions(filterOptions.availableLabels),
    topicOptions: createMultiSelectOptions(filterOptions.availableTopics),
    languageOptions: createMultiSelectOptions(filterOptions.availableLanguages),
  };
}

/**
 * Check if session matches status filter
 */
function matchesStatusFilter<T extends { status: string }>(session: T, statusFilter: string[]): boolean {
  return statusFilter.length === 0 || statusFilter.includes(session.status);
}

/**
 * Check if session matches array-based filter (case-insensitive)
 */
function matchesArrayFilter(sessionValues: string[] | undefined, filterValues: string[]): boolean {
  if (filterValues.length === 0) return true;
  if (!sessionValues || sessionValues.length === 0) return false;

  const lowerCaseFilter = filterValues.map(value => value.toLowerCase());

  return sessionValues.some(value => lowerCaseFilter.includes(value.toLowerCase()));
}

/**
 * Helper function to apply filters to audit sessions
 */
export function applyFilters<
  T extends {
    status: string;
    sampleParameters?: {
      filters?: {
        labels?: string[];
        topics?: string[];
        languages?: string[];
      };
    };
  },
>(sessions: T[], filters: AuditSessionFilters): T[] {
  return sessions.filter(
    session =>
      matchesStatusFilter(session, filters.status) &&
      matchesArrayFilter(session.sampleParameters?.filters?.labels, filters.labels) &&
      matchesArrayFilter(session.sampleParameters?.filters?.topics, filters.topics) &&
      matchesArrayFilter(session.sampleParameters?.filters?.languages, filters.languages)
  );
}

/**
 * Compare two sessions by created date
 */
function compareByCreatedAt<T extends { createdAt: string }>(a: T, b: T): number {
  const dateA = new Date(a.createdAt).getTime();
  const dateB = new Date(b.createdAt).getTime();

  return dateA - dateB;
}

/**
 * Compare two sessions by completed date
 */
function compareByCompletedAt<T extends { completedAt?: string }>(a: T, b: T): number {
  if (a.completedAt && b.completedAt) {
    const dateA = new Date(a.completedAt).getTime();
    const dateB = new Date(b.completedAt).getTime();

    return dateA - dateB;
  } else if (a.completedAt && !b.completedAt) {
    // Completed items come first when sorting by completion date
    return -1;
  } else if (!a.completedAt && b.completedAt) {
    // Incomplete items come last when sorting by completion date
    return 1;
  } else {
    // Both incomplete, maintain original order
    return 0;
  }
}

/**
 * Compare two sessions by items count
 */
function compareByItemsCount<T extends { statistics: { totalItems: number } }>(a: T, b: T): number {
  return a.statistics.totalItems - b.statistics.totalItems;
}

/**
 * Sort audit sessions based on the provided sort configuration
 */
export function sortAuditSessions<T extends { createdAt: string; completedAt?: string; statistics: { totalItems: number } }>(
  sessions: T[],
  sort: { field: 'createdAt' | 'completedAt' | 'itemsCount'; direction: 'asc' | 'desc' }
): T[] {
  return sessions.slice().sort((a, b) => {
    let comparison = 0;

    switch (sort.field) {
      case 'createdAt':
        comparison = compareByCreatedAt(a, b);
        break;
      case 'completedAt':
        comparison = compareByCompletedAt(a, b);
        break;
      case 'itemsCount':
        comparison = compareByItemsCount(a, b);
        break;
      default:
        comparison = 0;
    }

    // Apply sort direction
    return sort.direction === 'asc' ? comparison : -comparison;
  });
}
